/*
 * The product index: /product_hidden (PRODUCT_INDEX_ROUTE), screen
 * product.overview. Not a step: no step bar, not in the nav or the Contents.
 * The sidebar's "All products" opens it; "Back to the demo" returns to the
 * last in-app page the visitor saw (Q13). With none (a direct load) it opens
 * the intro, /product, at rest (state via: 'step-back', as the step bar's Back
 * does), so it never auto-plays the visitor onward.
 *
 * A dark summary card (stats, stage filters, "N of M opened"), then one group
 * per stage (Attract, Deliver, Counsel, Growth, then Foundation), each with a
 * stage chip and a regular grid of equal cards: icon, name, one-line blurb and
 * the stage's colour. The filters show a single stage (Foundation only under
 * All).
 */
import { useState } from 'react';
import { Link, useLocation, useOutletContext } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, CircleCheck } from 'lucide-react';
import Screen from '../../demo/Screen';
import { PRODUCT_INDEX_ROUTE, PRODUCTS, STAGES } from '../../demo/steps';
import { useVisited } from '../../demo/useVisited';
import './Overview.css';

const STATS = [
  { key: 'biomarkers', value: '1,500+', label: 'biomarkers' },
  { key: 'reports', value: '10M+', label: 'reports delivered' },
  { key: 'rating', value: '4.9/5', label: 'rated by patients' },
];

/* What each stage is for, under its chip. */
const STAGE_LINES = {
  attract: 'Bring patients in',
  deliver: 'What they receive',
  counsel: "Guide what's next",
  growth: 'Learn and reach',
  foundation: 'Under every product',
};

/*
 * App.js and steps.js both reach this page, so read STAGES and PRODUCTS at
 * render time, never at module level.
 */
function filterChips() {
  return [{ key: 'all', label: 'All', number: null }].concat(
    STAGES.filter((s) => s.number !== null).map((s) => ({ key: s.key, label: s.label, number: s.number })),
  );
}

function stageLabel(key) {
  return STAGES.find((s) => s.key === key)?.label ?? key;
}

function ProductCard({ product, visited, index }) {
  const Icon = product.icon;
  return (
    <li className="povw-cell">
      <Link
        to={product.route}
        className={`povw-card povw-card--${product.stage} hint`}
        style={{ '--hint-i': index }}
        aria-label={visited ? `Open ${product.name}, visited` : `Open ${product.name}`}
        data-trapezium-id={`product.overview.open-${product.slug}`}
      >
        <span className="povw-card__top">
          <span className="icon-tile povw-card__tile" aria-hidden="true">
            <Icon size={24} strokeWidth={1.9} />
          </span>
          <span className="povw-card__arrow" aria-hidden="true">
            <ArrowUpRight size={16} strokeWidth={2.2} />
          </span>
        </span>
        <h3 className="povw-card__name">
          {product.name}
          {visited && <CircleCheck className="povw-card__tick" size={18} strokeWidth={2.4} aria-hidden="true" />}
        </h3>
        <span className="povw-card__blurb">{product.blurb}</span>
      </Link>
    </li>
  );
}

/*
 * Where "Back to the demo" goes, decided on arrival. StepLayout's
 * lastLocationRef still holds the previous in-app location while this page
 * first renders (null on a first load). Every target carries via: 'step-back',
 * so the intro, if that is where it leads, starts at rest; other pages ignore it.
 */
function useBackTarget() {
  const location = useLocation();
  const outlet = useOutletContext();
  const [target] = useState(() => {
    const previous = outlet?.lastLocationRef?.current;
    const usable =
      previous &&
      previous.key !== location.key &&
      previous.pathname.replace(/\/+$/, '') !== PRODUCT_INDEX_ROUTE;
    return usable ? { pathname: previous.pathname, search: previous.search, hash: previous.hash } : '/product';
  });
  return target;
}

export default function Overview() {
  const [filter, setFilter] = useState('all');
  const backTo = useBackTarget();
  const { visited } = useVisited();

  const stages = filter === 'all' ? STAGES : STAGES.filter((s) => s.key === filter);
  const shownCount = PRODUCTS.filter((p) => stages.some((s) => s.key === p.stage)).length;
  const opened = PRODUCTS.filter((p) => visited.has(p.route)).length;
  const status =
    filter === 'all'
      ? `Showing all ${shownCount} products`
      : `Showing ${shownCount} ${stageLabel(filter)} products`;

  return (
    <Screen id="product.overview" title="All products" className="povw">
      <Link
        to={backTo}
        state={{ via: 'step-back' }}
        className="povw-back"
        data-trapezium-id="product.overview.back-to-demo"
      >
        <ArrowLeft size={18} strokeWidth={2.2} aria-hidden="true" />
        Back to the demo
      </Link>

      <div className={`povw-index povw-index--${filter}`} data-trapezium-state={`filter-${filter}`}>
        <section className="povw-hero" aria-labelledby="povw-title">
          <div className="povw-hero__lead">
            <p className="povw-hero__eyebrow">All products</p>
            <h1 id="povw-title" className="headline povw-hero__title">
              Everything a patient sees, from one team.
            </h1>
          </div>
          <dl className="povw-stats">
            {STATS.map((s) => (
              <div key={s.key}>
                <dt>{s.value}</dt>
                <dd>{s.label}</dd>
              </div>
            ))}
          </dl>
          <div className="povw-filters" role="group" aria-label="Filter products by stage">
            {filterChips().map((f, i) => {
              const pressed = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  className={`povw-chip hint${pressed ? ' is-active hint--active' : ''}`}
                  style={{ '--hint-i': i }}
                  aria-pressed={pressed}
                  onClick={() => setFilter(f.key)}
                  data-trapezium-id={`product.overview.filter-${f.key}`}
                >
                  {f.number && (
                    <span className="povw-chip__num" aria-hidden="true">
                      {f.number}
                    </span>
                  )}
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="povw-progress">
            <span className="povw-progress__track" aria-hidden="true">
              <span style={{ width: `${(opened / PRODUCTS.length) * 100}%` }} />
            </span>
            <span>{`${opened} of ${PRODUCTS.length} opened`}</span>
          </div>
          <p className="sr-only" role="status">
            {status}
          </p>
        </section>

        {stages.map((stage) => {
          const products = PRODUCTS.filter((p) => p.stage === stage.key);
          const titleId = `povw-stage-${stage.key}`;
          return (
            <section key={stage.key} className={`povw-stage povw-stage--${stage.key}`} aria-labelledby={titleId}>
              <div className="povw-stage__head">
                <h2 id={titleId} className="povw-stage__chip">
                  {stage.number && (
                    <span className="povw-stage__num" aria-hidden="true">
                      {stage.number}
                    </span>
                  )}
                  {stage.label}
                </h2>
                <p className="povw-stage__line">{STAGE_LINES[stage.key]}</p>
                <span className="povw-stage__rule" aria-hidden="true" />
              </div>
              <ul className="povw-cards">
                {products.map((p) => (
                  <ProductCard
                    key={p.slug}
                    product={p}
                    visited={visited.has(p.route)}
                    index={PRODUCTS.indexOf(p)}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </Screen>
  );
}
